import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import { MutatingDots } from "react-loader-spinner";
import Typography from "@mui/material/Typography";
import { FormattedMessage } from "react-intl";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";
import Avatar from "@mui/material/Avatar";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import XIcon from "@mui/icons-material/X";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkIcon from "@mui/icons-material/Link";
import { cleanEmptyHTMLTags } from "./hooks/cleanEmptyHTMLTags";
import { useDispatch, useSelector } from "react-redux";
import { getSpeakerDetails, getSpeakers } from "./store/slices/speakers";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import RoomIcon from "@mui/icons-material/Room";
import SpeakerShareModal from "./component/SpeakerShareModal";
import PoweredBy from "./component/PoweredBy";
import WidgetTitle from "./component/WidgetTitle";
import SearchBox from "./component/SearchBox";

const SpeakersWidget = ({
	language,
	eventID,
	organiserID,
	color,
	displayTitle,
	headerTitle,
	displaySearch,
	autoOpen,
	speakerSharingEnabled,
	topics,
	limit,
	imageType,
	grid,
}) => {
	const dispatch = useDispatch();
	const {
		speakers: _speakers,
		loading,
		error,
		speaker,
		speakerLoading,
	} = useSelector((state) => state.speakers);

	const queryParams = new URLSearchParams(window?.location?.search ?? "");
	const speakers = _speakers?.[topics];
	const queryQuery = queryParams?.get("q") ?? "";
	const [searchTerm, setSearchTerm] = useState(queryQuery);
	const queryId = queryParams?.get("id");
	const [open, setOpen] = useState(false);
	const [shareAnchorEl, setShareAnchorEl] = useState(null);

	useEffect(() => {
		dispatch(
			getSpeakers({
				organiserID: organiserID,
				eventID: eventID,
				language: language,
				topics: topics,
				limit: limit,
			})
		);
	}, [eventID, organiserID, language, topics, limit, dispatch]);

	const handleOpen = () => {
		setOpen(true);
	};
	const handleClose = () => {
		setOpen(false);
		setShareAnchorEl(null);
	};

	const handleGetSpeakerDetail = useCallback(
		(item) => {
			dispatch(
				getSpeakerDetails({
					organiserID: organiserID,
					eventID: eventID,
					language: language,
					id: item?.id,
				})
			);
			if (!loading) {
				handleOpen();
				if (queryId !== item?.id) {
					const url = new URL(window?.location.href);
					url.searchParams.set("speakerName", item?.name);
					url.searchParams.set("id", item?.id);
					if (queryQuery) url.searchParams.set("q", queryQuery);
					window?.history.pushState(null, "", url.toString());
				}
			}
		},
		[dispatch, eventID, language, loading, organiserID, queryId, queryQuery]
	);

	useEffect(() => {
		if (
			!loading &&
			speakers?.length > 0 &&
			queryId &&
			speakers.some((s) => s.id === queryId) &&
			autoOpen
		) {
			handleGetSpeakerDetail({ id: queryId });
		}
	}, [loading, speakers, queryId, handleGetSpeakerDetail, autoOpen]);

	const filterSpeakers = (Array.isArray(speakers) ? speakers : []).filter(
		(speaker) =>
			(speaker.name || "").toLowerCase().includes(searchTerm.toLowerCase())
	);

	if (loading || error) {
		return (
			<Box className="widget-spinner">
				<MutatingDots
					visible={true}
					height="100"
					width="100"
					color="#4fa94d"
					secondaryColor="#4fa94d"
					radius="12.5"
					ariaLabel="mutating-dots-loading"
					wrapperStyle={{}}
					wrapperClass=""
				/>
			</Box>
		);
	}

	return (
		<Box
			className="widget-block"
			style={{
				"--color": color,
			}}
		>
			{displayTitle && (
				<WidgetTitle title={headerTitle}><FormattedMessage id="speaker" /></WidgetTitle>
			)}
			{displaySearch && (
				<Box className="widget-block--search-container">
					<SearchBox
						value={searchTerm}
						onChange={setSearchTerm}
						placeholderId="searchSpeaker"
						count={
							<FormattedMessage
								id="speakerCount"
								values={{ count: filterSpeakers.length }}
							/>
						}
					/>
				</Box>
			)}
			{!loading && filterSpeakers.length > 0 ? (
				<Box
					className="widget-block--speakers"
					style={{ marginTop: displaySearch ? "30px" : 0 }}
					sx={{
						gridTemplateColumns: {
							xs: "repeat(2, 1fr)", // mobil
							sm: "repeat(3, 1fr)", // 600px+
							md: `repeat(${grid}, 1fr)`, // 1024px+
							lg: `repeat(${grid}, 1fr)`, // 1440px+
						},
					}}
				>
					{filterSpeakers.map((item, index) =>
						imageType === "round" ? (
							<Box
								key={index}
								className="widget-block--speakers--item-round"
								onClick={() => {
									handleGetSpeakerDetail(item);
								}}
							>
								<Box className="widget-block--speakers--item-round--image">
									<img src={item?.speakerImagePath} alt={item?.name} />
								</Box>
								<Box className="widget-block--speakers--item-round--header">
									<Box
										className="widget-block--speakers--item-round--header--name"
										dangerouslySetInnerHTML={{ __html: item?.name }}
									/>
									<Box
										className="widget-block--speakers--item-round--header--title"
										dangerouslySetInnerHTML={{ __html: item?.title }}
									/>
									<Box
										className="widget-block--speakers--item-round--header--companyName"
										dangerouslySetInnerHTML={{ __html: item?.companyName }}
									/>
								</Box>
							</Box>
						) : (
							<Box
								key={index}
								className="widget-block--speakers--item-square"
								onClick={() => {
									handleGetSpeakerDetail(item);
								}}
							>
								<Box className="widget-block--speakers--item-square--image">
									<img src={item?.speakerImagePath} alt={item?.name} />
								</Box>
								<Box className="widget-block--speakers--item-square--header">
									<Box
										className="widget-block--speakers--item-square--header--name"
										dangerouslySetInnerHTML={{ __html: item?.name }}
									/>
									<Box
										className="widget-block--speakers--item-square--header--title"
										dangerouslySetInnerHTML={{ __html: item?.title }}
									/>
									<Box
										className="widget-block--speakers--item-square--header--companyName"
										dangerouslySetInnerHTML={{ __html: item?.companyName }}
									/>
								</Box>
							</Box>
						)
					)}
				</Box>
			) : (
				<Box className="widget-block--search-not">
					<Alert severity="info" sx={{ fontSize: "14px" }}>
						<FormattedMessage id="notFoundSpeaker" />
					</Alert>
				</Box>
			)}
			{open && (
				<Modal open={open} aria-labelledby="modal-title" onClose={handleClose}>
					<Box className="widget-block--speakers--modal">
						{speakerLoading ? (
							<Box className="widget-block--speakers--modal--spinner">
								<MutatingDots
									visible={true}
									height="100"
									width="100"
									color="#4fa94d"
									secondaryColor="#4fa94d"
									radius="12.5"
									ariaLabel="mutating-dots-loading"
									wrapperStyle={{}}
									wrapperClass=""
								/>
							</Box>
						) : (
							<>
								<SpeakerShareModal
									speakerId={speaker?.id}
									speakerName={speaker?.name}
									anchorEl={shareAnchorEl}
									handleClose={() => setShareAnchorEl(null)}
								/>
								<Box
									sx={{
										textAlign: "center",
										borderBottom: ".5px solid #d6d9d9",
									}}
								>
									<IconButton
										onClick={handleClose}
										sx={{
											position: "absolute",
											top: 8,
											right: 8,
											cursor: "pointer",
										}}
									>
										<CloseIcon />
									</IconButton>
									<Typography
										variant="h6"
										sx={{
											fontWeight: "bold",
											pb: "1em",
											color: color,
											fontSize: "20px",
										}}
									>
										<FormattedMessage id="speakerDetail" />
									</Typography>
									{speakerSharingEnabled && (
										<IconButton
											onClick={(e) => setShareAnchorEl(e.currentTarget)}
											sx={{
												position: "absolute",
												top: 8,
												left: 8,
												cursor: "pointer",
											}}
										>
											<ShareIcon />
										</IconButton>
									)}
								</Box>
								<Box className="widget-block--speakers--modal--speaker--container">
									<Box className="widget-block--speakers--modal--speaker--header">
										<Avatar
											alt="Remy Sharp"
											src={speaker?.speakerImagePath}
											className="widget-block--speakers--modal--speaker--header--avatar"
										/>
										<Box>
											<Typography
												variant="h5"
												className="widget-block--speakers--modal--speaker--header--name"
											>
												{speaker?.name}
											</Typography>
											<Typography
												variant="body2"
												color="textSecondary"
												sx={{ textAlign: "center", fontSize: "14px" }}
												dangerouslySetInnerHTML={{ __html: speaker?.title }}
											/>
										</Box>
										<Box
											className="widget-block--speakers--modal--speaker--header--companyName"
											dangerouslySetInnerHTML={{
												__html: speaker?.companyName,
											}}
										/>
										<Box className="widget-block--speakers--modal--speaker--header--social-media">
											{speaker.socialMedia?.linkend && (
												<IconButton
													component="a"
													href={speaker.socialMedia?.linkend}
													target="_blank"
													rel="noopener noreferrer"
													aria-label="LinkedIn"
												>
													<LinkedInIcon />
												</IconButton>
											)}
											{speaker.socialMedia?.twitter && (
												<IconButton
													component="a"
													href={speaker.socialMedia?.twitter}
													target="_blank"
													rel="noopener noreferrer"
													aria-label="X (formerly Twitter)"
												>
													<XIcon />
												</IconButton>
											)}

											{speaker.socialMedia?.instagram && (
												<IconButton
													component="a"
													href={speaker.socialMedia?.instagram}
													target="_blank"
													rel="noopener noreferrer"
													aria-label="Instagram"
												>
													<InstagramIcon />
												</IconButton>
											)}

											{speaker.socialMedia?.facebook && (
												<IconButton
													component="a"
													href={speaker.socialMedia?.facebook}
													target="_blank"
													rel="noopener noreferrer"
													aria-label="Facebook"
												>
													<FacebookIcon />
												</IconButton>
											)}

											{speaker.socialMedia?.youtube && (
												<IconButton
													component="a"
													href={speaker.socialMedia?.youtube}
													target="_blank"
													rel="noopener noreferrer"
													aria-label="YouTube"
												>
													<YouTubeIcon />
												</IconButton>
											)}

											{speaker.socialMedia?.website && (
												<IconButton
													component="a"
													href={speaker.socialMedia?.website}
													target="_blank"
													rel="noopener noreferrer"
													aria-label="YouTube"
												>
													<LinkIcon />
												</IconButton>
											)}
										</Box>
										{speaker?.topics.length > 0 && (
											<Box className="widget-block--speakers--modal--speaker--header--topics">
												{speaker?.topics.map((t, kt) => (
													<Typography
														variant="span"
														key={kt}
														className="widget-block--speakers--modal--speaker--header--topics--title"
														sx={{ backgroundColor: color ?? "#000" }}
													>
														{t?.title}
													</Typography>
												))}
											</Box>
										)}
									</Box>
									<Box className="widget-block--speakers--modal--speaker--bio">
										<Box
											sx={{
												textAlign: "left",
												width: "100%",
												overflowY: "auto",
												fontFamily: '"Poppins", sans-serif',
												color: "#818181",
											}}
											dangerouslySetInnerHTML={{
												__html: cleanEmptyHTMLTags(speaker?.biography),
											}}
										/>
									</Box>
									{speaker?.programs && speaker?.programs.length > 0 && (
										<Box className="widget-block--program--modal--speaker--session-box">
											{speaker?.programs.map((item, key) => (
												<Box
													className="widget-block--program--modal--speaker--session-box--item"
													key={key}
												>
													<Box className="widget-block--program--modal--speaker--session-box--item--header">
														<Box className="widget-block--program--modal--speaker--session-box--item--header--left">
															<Box className="widget-block--program--modal--speaker--session-box--item--header--left--item">
																<AccessTimeIcon />
																<Typography variant="span">
																	{item?.startTime} - {item?.endTime}{" "}
																</Typography>
															</Box>
															<Box className="widget-block--program--modal--speaker--session-box--item--header--left--item">
																<RoomIcon />
																<Typography variant="span">
																	{item?.hall.name}
																</Typography>
															</Box>
														</Box>
														<Box className="widget-block--program--modal--speaker--session-box--item--header--right">
															<CalendarMonthIcon />
															<Typography variant="span">
																{item?.day.date}
															</Typography>
														</Box>
													</Box>
													<Box className="widget-block--program--modal--speaker--session-box--item--body">
														<Typography variant="span">
															{item?.title}
														</Typography>
													</Box>
												</Box>
											))}
										</Box>
									)}
								</Box>
							</>
						)}
					</Box>
				</Modal>
			)}
			<PoweredBy />
		</Box>
	);
};

export default SpeakersWidget;
